import React, { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Alert, AlertDescription } from "../ui/alert";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import {
  CheckCircle2, Loader2, Building2, User, AlertTriangle, Search, X,
} from "lucide-react";

/**
 * FundCaseDialog — the final review screen before a loan is created.
 *
 * Layout is deliberately linear so an admin can eyeball everything top-down
 * and click Fund with one keystroke:
 *
 *   1. Applicant summary  (read-only, sourced from the application)
 *   2. Loan terms         (admin picks every time — no silent defaults)
 *   3. Firm section       (radio: use matched | pick another existing | create new)
 *   4. Attorney section   (radio: use matched | pick another existing | create new)
 *   5. Optional note
 *
 * Any explicit edit typed here becomes the source of truth — the backend
 * upsert writes the admin's values verbatim (empty strings are ignored
 * so we never blank out good existing data).
 */
export function FundCaseDialog({ appId, open, onOpenChange, onFunded }) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Loan terms
  const [terms, setTerms] = useState({
    principal: "",
    funding_date: new Date().toISOString().slice(0, 10),
    monthly_rate_pct: "3.8",
    tier_months: "6",
    max_tiers: "6",
    wire_memo: "",
    note: "",
  });
  const setT = (k, v) => setTerms((f) => ({ ...f, [k]: v }));

  // Firm picker: mode = "matched" | "other" | "new"
  const [firmMode, setFirmMode] = useState("matched");
  const [firmSelected, setFirmSelected] = useState(null); // when mode === "other"
  const [firmFields, setFirmFields] = useState({ firm_name: "", firm_phone: "", firm_address: "" });
  const setF = (k, v) => setFirmFields((s) => ({ ...s, [k]: v }));

  // Attorney picker: mode = "matched" | "other" | "new"
  const [atyMode, setAtyMode] = useState("matched");
  const [atySelected, setAtySelected] = useState(null);
  const [atyFields, setAtyFields] = useState({ attorney_name: "", attorney_email: "", attorney_phone: "" });
  const setA = (k, v) => setAtyFields((s) => ({ ...s, [k]: v }));

  // ----- Load the preview & seed all form state -----
  useEffect(() => {
    if (!open || !appId) return;
    setLoading(true);
    setPreview(null);
    api.post(`/operations/applications/${appId}/fund/preview`)
      .then(({ data }) => {
        setPreview(data);
        const a = data.application;
        setTerms((prev) => ({
          ...prev,
          principal: String(a.funding_recommendation || ""),
          monthly_rate_pct: String(a.monthly_rate_pct ?? "3.8"),
          tier_months: String(a.tier_months ?? "6"),
          max_tiers: String(a.max_tiers ?? "6"),
        }));
        // Firm defaults
        if (data.matched_firm) {
          setFirmMode("matched");
          setFirmFields({
            firm_name: data.matched_firm.firm_name || "",
            firm_phone: data.matched_firm.phone || "",
            firm_address: data.matched_firm.address || "",
          });
        } else {
          setFirmMode("new");
          setFirmFields({
            firm_name: a.attorney_firm || "",
            firm_phone: a.attorney_phone || "",
            firm_address: "",
          });
        }
        // Attorney defaults
        if (data.existing_attorney) {
          setAtyMode("matched");
          setAtyFields({
            attorney_name: data.existing_attorney.name || a.attorney_name || "",
            attorney_email: data.existing_attorney.email || a.attorney_email || "",
            attorney_phone: data.existing_attorney.phone || a.attorney_phone || "",
          });
        } else {
          setAtyMode("new");
          setAtyFields({
            attorney_name: a.attorney_name || "",
            attorney_email: a.attorney_email || "",
            attorney_phone: a.attorney_phone || "",
          });
        }
        setFirmSelected(null);
        setAtySelected(null);
      })
      .catch(() => toast.error("Couldn't load application preview"))
      .finally(() => setLoading(false));
  }, [open, appId]);

  const alreadyFunded = !!preview?.existing_loan;
  const matchedFirm = preview?.matched_firm;
  const matchedAttorney = preview?.existing_attorney;

  // ----- Effective firm/attorney for the request payload -----
  const effective = useMemo(() => {
    // Which firm_id (if any) to attach to?
    let attach_to_firm_id = null;
    let firm_name = null;
    let firm_phone = null;
    let firm_address = null;
    if (firmMode === "matched" && matchedFirm) {
      attach_to_firm_id = matchedFirm.firm_id;
      firm_name = firmFields.firm_name;   // admin edits still applied
      firm_phone = firmFields.firm_phone;
      firm_address = firmFields.firm_address;
    } else if (firmMode === "other" && firmSelected) {
      attach_to_firm_id = firmSelected.firm_id;
    } else {
      firm_name = firmFields.firm_name;
      firm_phone = firmFields.firm_phone;
      firm_address = firmFields.firm_address;
    }
    // Attorney overrides.
    let attorney_name = atyFields.attorney_name;
    let attorney_email = atyFields.attorney_email;
    let attorney_phone = atyFields.attorney_phone;
    if (atyMode === "other" && atySelected) {
      // Picking a different existing attorney by email → let backend re-parent.
      attorney_name = atySelected.name || atyFields.attorney_name;
      attorney_email = atySelected.email;
      attorney_phone = atyFields.attorney_phone;
    }
    return {
      attach_to_firm_id,
      firm_name: firm_name || null,
      firm_phone: firm_phone || null,
      firm_address: firm_address || null,
      attorney_name: attorney_name || null,
      attorney_email: attorney_email || null,
      attorney_phone: attorney_phone || null,
    };
  }, [firmMode, matchedFirm, firmSelected, firmFields, atyMode, matchedAttorney, atySelected, atyFields]);

  const submit = async () => {
    const principal = Number(terms.principal);
    if (!(principal > 0)) return toast.error("Principal must be greater than 0");
    if (!terms.funding_date) return toast.error("Funding date is required");
    if (firmMode === "other" && !firmSelected) return toast.error("Pick a firm from the list");
    if (atyMode === "other" && !atySelected) return toast.error("Pick an attorney from the list");
    setSubmitting(true);
    try {
      const body = {
        principal,
        funding_date: terms.funding_date,
        monthly_rate_pct: Number(terms.monthly_rate_pct),
        tier_months: Number(terms.tier_months),
        max_tiers: Number(terms.max_tiers),
        wire_memo: terms.wire_memo || null,
        note: terms.note || null,
        ...effective,
      };
      const { data } = await api.post(`/operations/applications/${appId}/fund`, body);
      if (data.created) toast.success(`Loan ${data.loan_id} created`);
      else toast.info("Loan already existed for this application");
      onFunded?.(data);
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[680px] max-h-[92vh] overflow-y-auto" data-testid="fund-case-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl text-[#0A2540]">Fund case — final review</DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="py-8 flex items-center justify-center text-[#6B7280]">
            <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Loading application…
          </div>
        ) : !preview ? (
          <div className="py-8 text-center text-[#6B7280]">No preview available.</div>
        ) : (
          <div className="space-y-6 py-2">
            {alreadyFunded ? (
              <Alert className="bg-[#ECFDF5] border-[#A7F3D0]">
                <CheckCircle2 className="h-4 w-4 text-[#059669]" />
                <AlertDescription className="text-[#065F46]">
                  This case is already funded (loan <strong>{preview.existing_loan.loan_id}</strong>). Nothing to do.
                </AlertDescription>
              </Alert>
            ) : null}

            {/* ---------- 1) Applicant ---------- */}
            <SummaryBlock title="Applicant" testid="fund-applicant">
              <div className="font-medium text-[#0A2540]">{preview.application.name}</div>
              <div className="text-xs text-[#6B7280] mt-0.5">
                {preview.application.email}{" · "}
                {preview.application.case_type.replace("_", " ")}{" · "}
                current status: <strong>{preview.application.status}</strong>
              </div>
            </SummaryBlock>

            {/* ---------- 2) Loan terms ---------- */}
            <Section title="Loan terms" testid="fund-terms-section">
              <div className="grid grid-cols-2 gap-4">
                <FieldWrap label="Principal ($)" required>
                  <Input type="number" step="100" min="1" value={terms.principal}
                         onChange={(e) => setT("principal", e.target.value)}
                         data-testid="fund-principal" />
                </FieldWrap>
                <FieldWrap label="Funding date" required>
                  <Input type="date" value={terms.funding_date}
                         onChange={(e) => setT("funding_date", e.target.value)}
                         data-testid="fund-date" />
                </FieldWrap>
                <FieldWrap label="Monthly rate (%)" required>
                  <Input type="number" step="0.1" min="0.1" max="50" value={terms.monthly_rate_pct}
                         onChange={(e) => setT("monthly_rate_pct", e.target.value)}
                         data-testid="fund-rate" />
                </FieldWrap>
                <FieldWrap label="Months per tier" required>
                  <Input type="number" step="1" min="1" max="24" value={terms.tier_months}
                         onChange={(e) => setT("tier_months", e.target.value)}
                         data-testid="fund-tier-months" />
                </FieldWrap>
                <FieldWrap label="Max tiers" required>
                  <Input type="number" step="1" min="1" max="24" value={terms.max_tiers}
                         onChange={(e) => setT("max_tiers", e.target.value)}
                         data-testid="fund-max-tiers" />
                </FieldWrap>
                <FieldWrap label="Wire memo (optional)">
                  <Input value={terms.wire_memo} onChange={(e) => setT("wire_memo", e.target.value)}
                         data-testid="fund-wire-memo" />
                </FieldWrap>
              </div>
            </Section>

            {/* ---------- 3) Firm ---------- */}
            <Section title="Firm" testid="fund-firm-section" icon={Building2}>
              <ModePicker
                testid="firm-mode"
                value={firmMode}
                onChange={setFirmMode}
                options={[
                  matchedFirm && { value: "matched", label: `Use matched — ${matchedFirm.firm_name}`, hint: matchedFirm.domains?.length ? `matched by email domain` : `matched by name` },
                  { value: "other", label: "Attach to a different existing firm" },
                  { value: "new", label: matchedFirm ? "Create a new firm instead" : "Create new firm" },
                ].filter(Boolean)}
              />
              {firmMode === "other" ? (
                <EntityPicker
                  kind="firm"
                  selected={firmSelected}
                  onSelect={setFirmSelected}
                  testid="firm-picker"
                />
              ) : null}
              {/* Editable fields whenever mode allows edits (all three modes
                  now — admin can polish the matched firm's phone/address too) */}
              <div className="grid grid-cols-2 gap-4 mt-4">
                <FieldWrap label="Firm name" span={2}>
                  <Input
                    value={firmFields.firm_name}
                    disabled={firmMode === "other"}
                    onChange={(e) => setF("firm_name", e.target.value)}
                    data-testid="fund-firm-name"
                  />
                </FieldWrap>
                <FieldWrap label="Firm phone">
                  <Input
                    value={firmFields.firm_phone}
                    disabled={firmMode === "other"}
                    onChange={(e) => setF("firm_phone", e.target.value)}
                    data-testid="fund-firm-phone"
                  />
                </FieldWrap>
                <FieldWrap label="Firm address">
                  <Input
                    value={firmFields.firm_address}
                    disabled={firmMode === "other"}
                    onChange={(e) => setF("firm_address", e.target.value)}
                    data-testid="fund-firm-address"
                  />
                </FieldWrap>
              </div>
              {firmMode === "new" && !matchedFirm ? (
                <Alert className="mt-3 bg-[#FFF7ED] border-[#FED7AA]">
                  <AlertTriangle className="h-4 w-4 text-[#C2410C]" />
                  <AlertDescription className="text-[#0A2540]">
                    A new firm will be created with these details.
                  </AlertDescription>
                </Alert>
              ) : null}
            </Section>

            {/* ---------- 4) Attorney ---------- */}
            <Section title="Attorney" testid="fund-attorney-section" icon={User}>
              <ModePicker
                testid="attorney-mode"
                value={atyMode}
                onChange={setAtyMode}
                options={[
                  matchedAttorney && { value: "matched", label: `Use matched — ${matchedAttorney.name || matchedAttorney.email}`, hint: matchedAttorney.email },
                  { value: "other", label: "Attach to a different existing attorney" },
                  { value: "new", label: matchedAttorney ? "Create a new attorney instead" : "Create new attorney" },
                ].filter(Boolean)}
              />
              {atyMode === "other" ? (
                <EntityPicker
                  kind="attorney"
                  selected={atySelected}
                  onSelect={setAtySelected}
                  testid="attorney-picker"
                />
              ) : null}
              <div className="grid grid-cols-2 gap-4 mt-4">
                <FieldWrap label="Attorney name">
                  <Input
                    value={atyFields.attorney_name}
                    disabled={atyMode === "other"}
                    onChange={(e) => setA("attorney_name", e.target.value)}
                    data-testid="fund-attorney-name"
                  />
                </FieldWrap>
                <FieldWrap label="Attorney email">
                  <Input
                    type="email"
                    value={atyFields.attorney_email}
                    disabled={atyMode !== "new"}
                    onChange={(e) => setA("attorney_email", e.target.value)}
                    data-testid="fund-attorney-email"
                  />
                </FieldWrap>
                <FieldWrap label="Attorney phone" span={2}>
                  <Input
                    value={atyFields.attorney_phone}
                    disabled={atyMode === "other"}
                    onChange={(e) => setA("attorney_phone", e.target.value)}
                    data-testid="fund-attorney-phone"
                  />
                </FieldWrap>
              </div>
            </Section>

            {/* ---------- 5) Note ---------- */}
            <Section title="Note (optional)" testid="fund-note-section">
              <textarea
                value={terms.note} onChange={(e) => setT("note", e.target.value)}
                rows={2} data-testid="fund-note"
                className="w-full rounded-md border border-[#E5E7EB] p-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0066FF]/30"
              />
            </Section>
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="fund-cancel">Cancel</Button>
          <Button
            onClick={submit}
            disabled={submitting || loading || alreadyFunded || !preview}
            className="bg-[#0066FF] hover:bg-[#0052CC]"
            data-testid="fund-submit"
          >
            {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Funding…</> : "Fund case"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================
// Sub-components
// ============================================================

function Section({ title, icon: Icon, testid, children }) {
  return (
    <fieldset data-testid={testid}>
      <legend className="text-[11px] uppercase tracking-[0.14em] font-semibold text-[#6B7280] mb-3 flex items-center gap-1.5">
        {Icon ? <Icon className="w-3.5 h-3.5" /> : null}
        {title}
      </legend>
      {children}
    </fieldset>
  );
}

function SummaryBlock({ title, testid, children }) {
  return (
    <div className="rounded-xl bg-[#F9FAFB] border border-[#E5E7EB] p-4" data-testid={testid}>
      <div className="text-[11px] uppercase tracking-[0.14em] font-semibold text-[#6B7280] mb-1">{title}</div>
      {children}
    </div>
  );
}

function FieldWrap({ label, required, span = 1, children }) {
  return (
    <div className={span === 2 ? "col-span-2" : ""}>
      <Label className="text-xs text-[#374151] mb-1.5 block">
        {label} {required ? <span className="text-[#DC2626]">*</span> : null}
      </Label>
      {children}
    </div>
  );
}

function ModePicker({ value, onChange, options, testid }) {
  return (
    <div className="flex flex-col gap-2" data-testid={testid}>
      {options.map((o) => (
        <label
          key={o.value}
          className={
            "flex items-start gap-3 rounded-lg border px-3 py-2.5 cursor-pointer transition-colors " +
            (value === o.value
              ? "border-[#0066FF] bg-[#F0F7FF]"
              : "border-[#E5E7EB] hover:border-[#9CA3AF]")
          }
        >
          <input
            type="radio"
            className="mt-1"
            checked={value === o.value}
            onChange={() => onChange(o.value)}
            data-testid={`${testid}-${o.value}`}
          />
          <div className="min-w-0">
            <div className="text-sm text-[#0A2540]">{o.label}</div>
            {o.hint ? <div className="text-xs text-[#6B7280] mt-0.5 truncate">{o.hint}</div> : null}
          </div>
        </label>
      ))}
    </div>
  );
}

/**
 * EntityPicker — type-ahead search over /operations/{firms|attorneys}/search.
 * Two shapes normalized into a single { id, primary, secondary } row.
 */
function EntityPicker({ kind, selected, onSelect, testid }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!q.trim()) { setResults([]); return; }
    let cancelled = false;
    setBusy(true);
    const t = setTimeout(async () => {
      try {
        const path = kind === "firm"
          ? `/operations/firms/search?q=${encodeURIComponent(q)}`
          : `/operations/attorneys/search?q=${encodeURIComponent(q)}`;
        const { data } = await api.get(path);
        const rows = Array.isArray(data) ? data : (data.firms || data.attorneys || []);
        if (!cancelled) setResults(rows.slice(0, 8));
      } catch { /* ignore */ } finally {
        if (!cancelled) setBusy(false);
      }
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [q, kind]);

  const clear = () => onSelect(null);

  if (selected) {
    return (
      <div className="mt-3 flex items-center gap-2 rounded-md bg-[#F0F7FF] border border-[#BFDBFE] px-3 py-2 text-sm text-[#0A2540]">
        {kind === "firm" ? <Building2 className="w-4 h-4 text-[#0066FF]" /> : <User className="w-4 h-4 text-[#0066FF]" />}
        <span className="font-medium truncate">
          {kind === "firm" ? (selected.firm_name || selected.name) : (selected.name || selected.email)}
        </span>
        <span className="text-xs text-[#6B7280] truncate">
          {kind === "firm"
            ? (selected.domains?.join(", ") || selected.firm_id)
            : `${selected.email}${selected.firm ? " · " + selected.firm : ""}`}
        </span>
        <button type="button" onClick={clear} className="ml-auto text-xs text-[#0066FF] hover:text-[#0052CC]" data-testid={`${testid}-clear`}>
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    );
  }

  return (
    <div className="mt-3">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CA3AF]" />
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={kind === "firm" ? "Search firms by name or domain…" : "Search attorneys by name or email…"}
          className="pl-9"
          data-testid={`${testid}-search`}
        />
      </div>
      {busy ? (
        <div className="mt-2 text-xs text-[#6B7280] flex items-center gap-1.5">
          <Loader2 className="w-3 h-3 animate-spin" /> searching…
        </div>
      ) : results.length > 0 ? (
        <ul className="mt-2 border border-[#E5E7EB] rounded-md divide-y divide-[#F3F4F6] max-h-56 overflow-y-auto bg-white" data-testid={`${testid}-results`}>
          {results.map((r) => {
            const key = kind === "firm" ? r.firm_id : r.email;
            const primary = kind === "firm" ? (r.firm_name || r.name) : (r.name || r.email);
            const secondary = kind === "firm"
              ? (r.domains?.[0] || r.firm_id)
              : `${r.email}${r.firm ? " · " + r.firm : ""}`;
            return (
              <li key={key}>
                <button
                  type="button"
                  onClick={() => { onSelect(r); setResults([]); setQ(""); }}
                  className="w-full text-left px-3 py-2 hover:bg-[#F9FAFB] flex items-center gap-2"
                  data-testid={`${testid}-option-${key}`}
                >
                  {kind === "firm" ? <Building2 className="w-4 h-4 text-[#0066FF]" /> : <User className="w-4 h-4 text-[#0066FF]" />}
                  <span className="text-sm text-[#0A2540] font-medium">{primary}</span>
                  <span className="text-xs text-[#6B7280] truncate">· {secondary}</span>
                </button>
              </li>
            );
          })}
        </ul>
      ) : q ? (
        <div className="mt-2 text-xs text-[#6B7280]">No matches. Consider &ldquo;Create new&rdquo; instead.</div>
      ) : null}
    </div>
  );
}
